import React from 'react'
import './style/v2-app-menu.scss'

import classnames from 'classnames'

import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { asCaslSubject, getDemoAttributesFor, getOrgDemoAttributesFor } from '../../stores/app-ability'
import { useService } from '@pushly/aqe/lib/hooks'
import { FEAT_MULTI_DOMAIN_NOTIFICATIONS } from '../../constants'
import { getSidebarPathModule } from './helpers'
import { FEAT_APP_MESSAGES } from '../../constants'
import { SIDEBAR_ICON_SIZE } from './constants'
import { FEAT_FEEDS_MGMT } from '@pushly/aqe/lib/constants'
import { preventDefault } from '@pushly/aqe/lib/utils'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core'
import { Menu, MenuProps } from 'antd'
import { AntdClassnameTokens } from '../../styles/antd-classname-tokens'
import { shouldShowCatalogNavItem } from '../../features/catalog/helpers'
import { ICON_ASSIGNMENTS, MenuIcon } from '@pushly/aqe/lib/icons'
import type { SlotId } from '@pushly/aqe/lib/icons'

type ItemType = NonNullable<MenuProps['items']>[number]

const appMenuKeyMap: Record<string, string> = {
    dashboard: '/dashboard',
    notifications: '/notifications',
    feeds: '/feeds',
    appMessages: '/app-messages',
    journeys: '/journeys',
    catalogs: '/catalogs',
    segments: '/segments',
    prompts: '/prompts',
    insights: '/insights',
    settings: '/#settings',
}

/** Display order and grouping. Unlabelled sections render as plain items. */
const MENU_SECTIONS: { label?: string; keys: string[] }[] = [
    { keys: ['dashboard', 'insights'] },
    { label: 'Messaging', keys: ['notifications', 'appMessages', 'journeys'] },
    { label: 'Audience', keys: ['segments', 'prompts'] },
    { label: 'Content', keys: ['feeds', 'catalogs'] },
    { keys: ['settings'] },
]

interface IAppMenu {
    /**
     * `main` omits Settings and `bottom` shows only Settings, so the sidebar can
     * seat Settings in its lower section. Default shows everything in one list.
     */
    placement?: 'all' | 'main' | 'bottom'
    /**
     * Items appended to the `bottom` menu that AppMenu does not route itself
     * (Help, the user menu). Their clicks go to `onExtraClick` instead.
     */
    extraItems?: ItemType[]
    onExtraClick?: NonNullable<MenuProps['onClick']>
}

const AppMenu = ({ placement = 'all', extraItems = [], onExtraClick }: IAppMenu) => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const userPreferences = useUserPreferences(appState.currentUser?.id)

    const buildMultiTierCondition = (domainEntity: SubjectEntity, orgEntity: SubjectEntity, orgFlag?) => {
        return () => {
            const entity = appState.inDomainContext ? domainEntity : orgEntity
            const abilityConstraints = appState.inDomainContext
                ? {
                      domainId: appState.currentDomain?.id,
                      ...getDemoAttributesFor(appState.currentDomain?.id, appState.currentDomain),
                  }
                : {
                      accountId: appState.currentDomain?.accountId,
                      ...getOrgDemoAttributesFor(appState.currentDomain?.accountId, appState.currentDomain),
                  }

            /**
             * Menu Visibility:
             * Domain Context  - user has read access to domain
             * Org Context     - org has key & user has read access to org
             */
            let can = appState.abilityStore.can(AbilityAction.READ, asCaslSubject(entity, abilityConstraints))
            if (can && appState.inOrgContext && orgFlag) {
                can = appState.currentDomain?.accountFlags?.includes(orgFlag) ?? false
            }

            return can ?? false
        }
    }

    const inOrgContext = appState.inOrgContext
    const routeEntity = inOrgContext ? 'org' : 'domain'

    const selectedPathKey = getSidebarPathModule(location.pathname)

    /** The sidebar item's Pushly icon: filled when it is the current page, outlined otherwise. */
    const slotIcon = (key: string) => {
        const name = ICON_ASSIGNMENTS[`nav:${key}` as SlotId]
        return name ? (
            <MenuIcon name={name} size={SIDEBAR_ICON_SIZE} current={key === selectedPathKey} aria-hidden="true" />
        ) : undefined
    }

    // access observable selected domain/account in render fn to ensure redraw
    const activeAcctId = appState.currentDomain?.accountId ?? 0
    const activeDomainId = appState.currentDomain?.id ?? 0

    if (appState?.abilityStore?.acs?.state && !appState.abilityStore.abilities.rules.length) {
        Promise.resolve(appState.abilityStore.acs.getAbilities())
    }

    const getMenuItems = (configs): ItemType[] => {
        const menuItems: ItemType[] = []

        configs.forEach((config) => {
            const disabled = !appState.currentDomain?.id
            const menuItem: ItemType = {
                key: config.key,
                disabled,
                icon: config.icon,
                // title: config.title,
                // A real link, so the browser's own open-in-new-tab and copy-link work and assistive tech
                // announces a link to a page. Clicks are still routed by the Menu's onClick, which prevents
                // the default navigation; the anchor stays out of the tab order (the Menu item is the one
                // keyboard stop) and the current page is announced with aria-current.
                label:
                    !disabled && config.key in appMenuKeyMap ? (
                        <a
                            href={appService.routeWithin(routeEntity, appMenuKeyMap[config.key], true)}
                            tabIndex={-1}
                            aria-current={config.key === selectedPathKey ? 'page' : undefined}
                        >
                            {config.title}
                        </a>
                    ) : (
                        config.title
                    ),
            }

            let canDisplay: boolean = true
            if (config.condition) {
                canDisplay = config.condition()
            }

            if (canDisplay) {
                menuItems.push(menuItem)
            }
        })

        return menuItems
    }

    const sidebarExpanded = userPreferences.get(['sidebar_expanded'])

    /**
     * Labelled sections. The group title is always rendered; collapsing the
     * sidebar shrinks it to a divider line in CSS, so the structure does not
     * change mid-animation. A section with no visible items is dropped.
     */
    const getSectionedItems = (sections: { label?: string; configs: any[] }[]): ItemType[] => {
        const result: ItemType[] = []

        sections.forEach(({ label, configs }) => {
            const children = getMenuItems(configs)
            if (!children.length) {
                return
            }

            if (label) {
                result.push({ type: 'group', key: `group-${label}`, label, children })
            } else {
                result.push(...children)
            }
        })

        return result
    }

    const isBottom = placement === 'bottom'

    const arrangeItems = (configs: any[]): ItemType[] => [
        ...getSectionedItems(
            MENU_SECTIONS.map(({ label, keys }) => ({
                label,
                configs: keys
                    .filter((key) => placement === 'all' || (key === 'settings') === (placement === 'bottom'))
                    .map((key) => configs.find((config) => config.key === key))
                    .filter(Boolean),
            })),
        ),
        ...(isBottom ? extraItems : []),
    ]

    return (
        <Menu
            className={classnames(
                'app-menu',
                `active-account-${activeAcctId}`,
                `active-domain-${activeDomainId}`,
                appState.abilityStore.acs?.state,
                {
                    collapsed: !sidebarExpanded,
                    open: sidebarExpanded,
                    'app-menu-bottom': isBottom,
                },
            )}
            theme="dark"
            mode="inline"
            inlineCollapsed={!sidebarExpanded}
            selectedKeys={!selectedPathKey ? [] : [selectedPathKey]}
            onClick={(info) => {
                const { key: itemKey, domEvent } = info
                if (!(itemKey in appMenuKeyMap)) {
                    onExtraClick?.(info)
                    return
                }

                preventDefault(domEvent)

                const currentPath = appService.currentRoute
                const newPath = appMenuKeyMap[itemKey]

                if ((domEvent as any).metaKey || (domEvent as any).ctrlKey) {
                    window.open(appService.routeWithin(routeEntity, newPath, true))
                } else if (currentPath !== newPath) {
                    appService.routeWithin(routeEntity, newPath)
                }
            }}
            classNames={AntdClassnameTokens.Menu({ prefix: 'sidebar' })}
            items={arrangeItems([
                {
                    key: 'dashboard',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('dashboard'),
                    title: 'Dashboard',
                    condition() {
                        const entity = appState.inDomainContext
                            ? appState.abilityStore.currentDomainIdentity
                            : appState.abilityStore.currentOrgIdentity

                        const canRead = appState.abilityStore.can(AbilityAction.READ, entity)

                        return canRead
                    },
                },
                {
                    key: 'notifications',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('notifications'),
                    title: 'Notifications',
                    condition: buildMultiTierCondition(
                        SubjectEntity.NOTIFICATION,
                        SubjectEntity.ORG_NOTIFICATION,
                        FEAT_MULTI_DOMAIN_NOTIFICATIONS,
                    ),
                },
                {
                    key: 'appMessages',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('appMessages'),
                    title: 'App Messages',
                    condition() {
                        return (
                            (appState.currentDomain?.flags.includes(FEAT_APP_MESSAGES) ?? false) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.APP_MESSAGE),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'journeys',
                    disabled: !appState.currentDomain?.id,
                    title: 'Journeys',
                    icon: slotIcon('journeys'),
                    condition() {
                        return (
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.CAMPAIGN),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'feeds',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('feeds'),
                    title: 'Feeds',
                    condition() {
                        return (
                            (appState.currentDomain?.flags.includes(FEAT_FEEDS_MGMT) ?? false) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.FEED),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'catalogs',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('catalogs'),
                    get title() {
                        const itemType = appState.currentDomain?.ecommConfig?.itemType
                        const label = itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1) : 'Item'
                        return `${label} Catalog`
                    },
                    condition() {
                        return (
                            shouldShowCatalogNavItem(appState.currentDomain ?? null) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                /* Org Segments will not have a separate flag for multi-domain level.
                    If an org has access to multi-domain notifications they will
                    also implicitly have access to segmentation.
                */
                {
                    key: 'segments',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('segments'),
                    title: 'Segments',
                    condition: buildMultiTierCondition(
                        SubjectEntity.SEGMENT,
                        SubjectEntity.ORG_SEGMENT,
                        FEAT_MULTI_DOMAIN_NOTIFICATIONS,
                    ),
                },
                {
                    key: 'prompts',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('prompts'),
                    title: 'Prompts',
                    condition() {
                        return (
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.currentDomainIdentity,
                            ) &&
                            appState.abilityStore.can(
                                AbilityAction.READ,
                                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.PROMPT),
                            ) &&
                            appState.inDomainContext
                        )
                    },
                },
                {
                    key: 'insights',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('insights'),
                    title: 'Insights',
                    condition() {
                        const entity = appState.inDomainContext
                            ? appState.abilityStore.currentDomainIdentity
                            : appState.abilityStore.currentOrgIdentity

                        return appState.abilityStore.can(AbilityAction.READ, entity)
                    },
                },
                {
                    key: 'settings',
                    disabled: !appState.currentDomain?.id,
                    icon: slotIcon('settings'),
                    title: 'Settings',
                    condition() {
                        const entity = appState.inDomainContext
                            ? appState.abilityStore.currentDomainIdentity
                            : appState.abilityStore.currentOrgIdentity

                        return appState.abilityStore.can(AbilityAction.UPDATE, entity)
                    },
                },
            ])}
        />
    )
}

export default AppMenu
